import { AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2 } from "@angular/core";

interface PlacedCell {
	cell: HTMLTableCellElement;
	col: number;
}

/**
 * Place les cellules de chaque ligne sur la grille du tableau, en tenant compte de colSpan et rowSpan :
 * une cellule fusionnée horizontalement occupe plusieurs colonnes, une cellule fusionnée verticalement
 * décale les cellules des lignes suivantes.
 */
function placeCells(rows: HTMLTableRowElement[]): PlacedCell[][] {
	// busy[c] : nombre de lignes encore occupées sur la colonne c par une cellule fusionnée verticalement
	const busy: number[] = [];

	return rows.map((row) => {
		const placed: PlacedCell[] = [];
		let col = 0;

		Array.from(row.cells).forEach((cell) => {
			while (busy[col] > 0) col++;
			placed.push({ cell, col });
			for (let i = 0; i < cell.colSpan; i++) {
				busy[col + i] = cell.rowSpan === 0 ? Infinity : cell.rowSpan;
			}
			col += cell.colSpan;
		});

		for (let c = 0; c < busy.length; c++) {
			if (busy[c] > 0) busy[c]--;
		}
		return placed;
	});
}

@Directive({
	selector: "table[appResponsiveTable]",
})
export class ResponsiveTableDirective implements AfterViewInit, OnDestroy {
	private observer: MutationObserver | null = null;
	private headers: string[] = [];
	// libellés posés par la directive : seuls ceux-là sont rafraîchis, un data-label écrit à la main reste intact
	private autoLabels = new WeakMap<Element, string>();

	constructor(
		private el: ElementRef<HTMLTableElement>,
		private renderer: Renderer2
	) {}

	ngAfterViewInit(): void {
		const table = this.el.nativeElement;
		this.renderer.addClass(table, "responsive-card-table");

		this.readHeaders();
		this.labelRows();

		this.observer = new MutationObserver(() => {
			this.readHeaders();
			this.labelRows();
		});

		// characterData : un en-tête dont le texte change sur place (ex. « Montant ({{ devise }}) »)
		this.observer.observe(table, { childList: true, subtree: true, characterData: true });
	}

	ngOnDestroy(): void {
		this.observer?.disconnect();
	}

	/**
	 * Un libellé par colonne de la grille. Avec plusieurs lignes d'en-tête (ligne de groupes puis ligne
	 * des colonnes), c'est le texte non vide le plus bas de la colonne qui est retenu.
	 */
	private readHeaders(): void {
		const headers: string[] = [];
		const head = this.el.nativeElement.tHead;

		if (head) {
			placeCells(Array.from(head.rows)).forEach((row) =>
				row.forEach(({ cell, col }) => {
					const text = cell.innerText.trim();
					if (!text) return;
					for (let i = 0; i < cell.colSpan; i++) headers[col + i] = text;
				})
			);
		}
		this.headers = headers;
	}

	private labelRows(): void {
		Array.from(this.el.nativeElement.tBodies).forEach((tbody) => {
			placeCells(Array.from(tbody.rows)).forEach((row) =>
				row.forEach(({ cell, col }) => {
					if (cell.tagName !== "TD") return;

					const label = this.headers[col];
					if (!label) return;

					const current = cell.getAttribute("data-label");
					if (current === null || current === this.autoLabels.get(cell)) {
						if (current !== label) {
							this.renderer.setAttribute(cell, "data-label", label);
						}
						this.autoLabels.set(cell, label);
					}
				})
			);
		});
	}
}
